/**
 * BooksSheet — list of books with active selection + edit / new entry.
 *
 * Mobile = bottom sheet. Desktop = centered modal. The presentational shape
 * is the same; only the wrapper differs (controlled by the `variant` prop —
 * the parent picks based on viewport).
 *
 * Pixel-faithful port of `BooksSheet` in `design/project/screens-home.jsx`:
 *   - color swatch chip on the left (token-driven)
 *   - book name + 「預設」 badge if `is_default`
 *   - check mark on the active row, chevron-right elsewhere
 *   - 「新增帳本」 dashed button at the bottom
 *
 * Sharp edges (audit):
 *   - The last remaining book is undeletable. We do NOT render the delete
 *     affordance from this sheet — `BookEditSheet` is the surface that
 *     enforces that, but defensively we surface the same toast if a row
 *     edit is requested for the only book.
 *   - Each row carries `is_default` so the "預設" badge stays in sync with
 *     the underlying store; we never compute it locally.
 */

import { useState, type CSSProperties } from 'react';

import BottomSheet from '@/components/common/BottomSheet';
import Modal from '@/components/common/Modal';
import Icon, { type IconName } from '@/components/common/Icon';
import type { BookRow } from '@/lib/sheets/types';
import { SEED_CATEGORIES, SEED_SUBCATEGORIES } from '@/lib/sheets/bootstrap';

export type BooksSheetVariant = 'sheet' | 'modal';

export interface BooksSheetProps {
  books: BookRow[];
  activeBookId: string | null;
  onActivate: (id: string) => void;
  onEdit: (book: BookRow) => void;
  onCreate: () => void;
  onClose: () => void;
  /** Defaults to 'sheet'. Desktop callers should pass 'modal'. */
  variant?: BooksSheetVariant;
}

const ROW_BTN_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 14px',
  background: 'var(--bg-raised)',
  border: 0,
  borderRadius: 14,
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};

function tokenToCss(token: string): string {
  // The slice stores either bare CSS-variable names ("--matcha-500") or full
  // `var(...)` literals. Normalise to a CSS color expression.
  if (token.startsWith('--')) return `var(${token})`;
  return token;
}

function BooksList(props: BooksSheetProps): JSX.Element {
  const { books, activeBookId, onActivate, onEdit, onCreate } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {books.map((b) => {
        const active = b.id === activeBookId;
        return (
          <div
            key={b.id}
            style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}
          >
            <button
              type="button"
              aria-current={active ? 'true' : undefined}
              onClick={() => onActivate(b.id)}
              style={ROW_BTN_BASE}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: tokenToCss(b.color_token),
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <Icon name="book" size={18} stroke="#fff" />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      font: '500 15px var(--font-cjk)',
                      color: 'var(--fg-1)',
                    }}
                  >
                    {b.name}
                  </span>
                  {/* v3 — typed badge so the user can tell 家庭 / 專案 / 個人 apart */}
                  <span
                    aria-label={`類型 ${b.type}`}
                    style={{
                      font: '500 10px var(--font-cjk)',
                      color: 'var(--fg-3)',
                      background: 'var(--bg-raised)',
                      padding: '1px 6px',
                      borderRadius: 999,
                    }}
                  >
                    {b.type === 'family' ? '家庭' : b.type === 'project' ? '專案' : '個人'}
                  </span>
                  {b.is_default && (
                    <span
                      style={{
                        font: '500 10px var(--font-cjk)',
                        color: 'var(--matcha-700)',
                        background: 'rgba(126,139,90,0.14)',
                        padding: '1px 6px',
                        borderRadius: 999,
                      }}
                    >
                      預設
                    </span>
                  )}
                </span>
              </span>
              {active ? (
                <Icon name="check" size={18} stroke="var(--accent)" />
              ) : (
                <Icon name="chevR" size={16} stroke="var(--fg-3)" />
              )}
            </button>
            <button
              type="button"
              aria-label={`編輯帳本 ${b.name}`}
              onClick={() => onEdit(b)}
              style={{
                width: 38,
                background: 'var(--bg-raised)',
                border: 0,
                borderRadius: 14,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--fg-3)',
                flexShrink: 0,
              }}
            >
              <Icon name="edit" size={16} stroke="var(--fg-3)" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={onCreate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px',
          background: 'transparent',
          border: '1px dashed var(--border-2)',
          borderRadius: 14,
          cursor: 'pointer',
          color: 'var(--fg-2)',
          textAlign: 'left',
          marginTop: 4,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            background: 'var(--bg-raised)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon name="plus" size={18} stroke="var(--fg-2)" />
        </span>
        <span style={{ font: '500 15px var(--font-cjk)' }}>新增帳本</span>
      </button>
    </div>
  );
}

export default function BooksSheet(props: BooksSheetProps): JSX.Element {
  const variant = props.variant ?? 'sheet';
  const list = <BooksList {...props} />;

  if (variant === 'modal') {
    return (
      <Modal open={true} onClose={props.onClose} title="帳本">
        {list}
      </Modal>
    );
  }
  return (
    <BottomSheet open={true} onClose={props.onClose} title="帳本">
      {list}
    </BottomSheet>
  );
}

// ─── New-book sheet ──────────────────────────────────────────────────────

/**
 * Book type, per design source. Stored alongside `name` + `color_token` in
 * the create payload — v3 schema persists this on the `BookRow.type`
 * column so 帳本 list can show a typed icon.
 */
export type BookType = 'personal' | 'family' | 'project';

const BOOK_TYPES: ReadonlyArray<{
  id: BookType;
  label: string;
  detail: string;
}> = [
  { id: 'personal', label: '個人帳本', detail: '只有你會看到' },
  { id: 'family', label: '家庭／共享帳本', detail: '邀請成員共同記帳' },
  { id: 'project', label: '專案帳本', detail: '有結束日期，如旅行、裝潢' },
];

const BOOK_ICONS: ReadonlyArray<IconName> = [
  'book',
  'home',
  'heart',
  'plane',
  'briefcase',
  'tag',
  'gift',
  'wallet',
];

export interface NewBookCreateInput {
  name: string;
  color_token: string;
  /** Book type — design source's three radio options. */
  type?: BookType;
  /** Icon key (one of the IconName constants). */
  icon?: IconName;
  /** v3: when non-null, the parent should ALSO seed copies of these main
   *  category ids (and their subs) into the new book. Empty array =
   *  the user opted out of seeding (book starts with no own cats and
   *  falls back to the system seeds visible everywhere). When undefined
   *  the parent treats it as "no extra seeding" too. */
  seedCategoryIds?: string[];
}

export interface NewBookSheetProps {
  onClose: () => void;
  onCreate: (input: NewBookCreateInput) => void;
  variant?: BooksSheetVariant;
}

const COLOR_SWATCHES: ReadonlyArray<{ token: string; label: string }> = [
  { token: '--terracotta-500', label: '赤土' },
  { token: '--matcha-500', label: '抹茶' },
  { token: '--honey-500', label: '蜂蜜' },
  { token: '--cat-transit', label: '晴空' },
  { token: '--cat-travel', label: '湖水' },
  { token: '--cat-pet', label: '奶茶' },
  { token: '--cat-tax', label: '鉛灰' },
  { token: '--mocha-600', label: '可可' },
  { token: '--cat-food', label: '南瓜' },
  { token: '--cat-fun', label: '蜜桃' },
  { token: '--cat-edu', label: '苔綠' },
  { token: '--cat-sub', label: '紫砂' },
];

export function NewBookSheet({
  onClose,
  onCreate,
  variant = 'sheet',
}: NewBookSheetProps): JSX.Element {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('--terracotta-500');
  const [type, setType] = useState<BookType>('personal');
  const [icon, setIcon] = useState<IconName>('book');
  // v3: 「複製預設分類？」 toggle. Default ON. When ON, the user picks
  // which mains to copy via the checklist below.
  const [seedOn, setSeedOn] = useState(true);
  const [seedExpanded, setSeedExpanded] = useState(false);
  const [checkedSeedIds, setCheckedSeedIds] = useState<Set<string>>(
    () => new Set(SEED_CATEGORIES.map((c) => c.id)),
  );
  const canSave = name.trim().length > 0;

  const toggleSeed = (id: string): void => {
    setCheckedSeedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = (): void => {
    if (!canSave) return;
    const seedCategoryIds: string[] = seedOn
      ? SEED_CATEGORIES.filter((c) => checkedSeedIds.has(c.id)).map((c) => c.id)
      : [];
    onCreate({
      name: name.trim(),
      color_token: color,
      type,
      icon,
      seedCategoryIds,
    });
  };

  const body = (
    <div>
      {/* Preview */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          padding: '12px 0 18px',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: tokenToCss(color),
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          <Icon name={icon} size={28} stroke="#fff" />
        </div>
        <div
          style={{
            font: '600 16px var(--font-cjk)',
            color: 'var(--fg-1)',
            minHeight: 22,
          }}
        >
          {name.trim() || '新帳本'}
        </div>
      </div>

      {/* Name */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 6,
          padding: '0 2px',
        }}
      >
        名稱
      </div>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={16}
        placeholder="例如：家庭、日本京都"
        style={{
          width: '100%',
          padding: '12px 14px',
          border: '0.5px solid var(--border-1)',
          borderRadius: 12,
          background: 'var(--bg-surface)',
          font: '500 15px var(--font-cjk)',
          color: 'var(--fg-1)',
          outline: 'none',
          boxSizing: 'border-box',
          marginBottom: 18,
        }}
      />

      {/* Type */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 8,
          padding: '0 2px',
        }}
      >
        類型
      </div>
      <div
        role="radiogroup"
        aria-label="帳本類型"
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 14,
          boxShadow: 'var(--shadow-1)',
          overflow: 'hidden',
          marginBottom: 18,
        }}
      >
        {BOOK_TYPES.map((t, i) => {
          const on = type === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setType(t.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '13px 14px',
                background: 'transparent',
                border: 0,
                borderBottom:
                  i < BOOK_TYPES.length - 1
                    ? '0.5px solid var(--border-1)'
                    : 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ flex: 1 }}>
                <span
                  style={{
                    display: 'block',
                    font: '500 15px var(--font-cjk)',
                    color: 'var(--fg-1)',
                  }}
                >
                  {t.label}
                </span>
                <span
                  style={{
                    display: 'block',
                    font: '400 12px var(--font-cjk)',
                    color: 'var(--fg-3)',
                    marginTop: 2,
                  }}
                >
                  {t.detail}
                </span>
              </span>
              <span
                aria-hidden="true"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  border: on
                    ? '6px solid var(--accent)'
                    : '1.5px solid var(--sand-300)',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Color */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 8,
          padding: '0 2px',
        }}
      >
        顏色
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 10,
          padding: '6px 0 16px',
        }}
      >
        {COLOR_SWATCHES.map((c) => {
          const on = c.token === color;
          return (
            <button
              key={c.token}
              type="button"
              aria-label={c.label}
              aria-pressed={on}
              onClick={() => setColor(c.token)}
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                background: tokenToCss(c.token),
                border: on ? '2.5px solid var(--fg-1)' : '2.5px solid transparent',
                boxShadow: on ? 'inset 0 0 0 2px #fff' : 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          );
        })}
      </div>

      {/* Icon */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 8,
          padding: '0 2px',
        }}
      >
        圖示
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 6,
          padding: '6px 0 16px',
        }}
      >
        {BOOK_ICONS.map((k) => {
          const on = icon === k;
          return (
            <button
              key={k}
              type="button"
              aria-label={`圖示 ${k}`}
              aria-pressed={on}
              onClick={() => setIcon(k)}
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: 10,
                background: on ? tokenToCss(color) : 'var(--bg-raised)',
                border: 0,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon
                name={k}
                size={18}
                stroke={on ? '#fff' : 'var(--fg-2)'}
              />
            </button>
          );
        })}
      </div>

      {/* Seed categories */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 8,
          padding: '0 2px',
        }}
      >
        預設分類
      </div>
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 14,
          boxShadow: 'var(--shadow-1)',
          marginBottom: 18,
          overflow: 'hidden',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            cursor: 'pointer',
            borderBottom: seedOn ? '0.5px solid var(--border-1)' : 0,
          }}
        >
          <input
            type="checkbox"
            aria-label="複製預設分類"
            checked={seedOn}
            onChange={(e) => setSeedOn(e.target.checked)}
          />
          <span style={{ flex: 1 }}>
            <span
              style={{
                display: 'block',
                font: '500 14px var(--font-cjk)',
                color: 'var(--fg-1)',
              }}
            >
              複製預設分類
            </span>
            <span
              style={{
                display: 'block',
                font: '400 11.5px var(--font-cjk)',
                color: 'var(--fg-3)',
                marginTop: 2,
              }}
            >
              將預設的 14 支出 + 5 收入分類複製到本帳本，可再自行調整
            </span>
          </span>
        </label>
        {seedOn && (
          <>
            <button
              type="button"
              onClick={() => setSeedExpanded((v) => !v)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                padding: '8px 14px',
                font: '500 12px var(--font-cjk)',
                color: 'var(--accent)',
                textAlign: 'left',
              }}
            >
              {seedExpanded ? '收起' : '選擇要複製哪些分類'}（{checkedSeedIds.size}/{SEED_CATEGORIES.length}）
            </button>
            {seedExpanded && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 4,
                  padding: '4px 10px 12px',
                }}
              >
                {SEED_CATEGORIES.map((c) => {
                  const on = checkedSeedIds.has(c.id);
                  return (
                    <label
                      key={c.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 8px',
                        cursor: 'pointer',
                        font: '400 12px var(--font-cjk)',
                        color: 'var(--fg-1)',
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={c.name}
                        checked={on}
                        onChange={() => toggleSeed(c.id)}
                      />
                      {c.kind === 'income' ? '收·' : '支·'}{c.name}
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Action row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          paddingTop: 6,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '10px 18px',
            background: 'transparent',
            border: '1px solid var(--border-1)',
            borderRadius: 999,
            cursor: 'pointer',
            font: '500 14px var(--font-cjk)',
            color: 'var(--fg-2)',
          }}
        >
          取消
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSave}
          style={{
            padding: '10px 18px',
            background: canSave ? 'var(--accent)' : 'var(--sand-300)',
            color: '#fff',
            border: 0,
            borderRadius: 999,
            cursor: canSave ? 'pointer' : 'default',
            font: '600 14px var(--font-cjk)',
          }}
        >
          建立
        </button>
      </div>
    </div>
  );

  if (variant === 'modal') {
    return (
      <Modal open={true} onClose={onClose} title="新增帳本" width={420}>
        {body}
      </Modal>
    );
  }
  return (
    <BottomSheet open={true} onClose={onClose} title="新增帳本">
      {body}
    </BottomSheet>
  );
}

// Re-export the seed dictionaries so callers (App.tsx onCreate handler)
// can build the per-book copies of mains + subs.
export { SEED_CATEGORIES, SEED_SUBCATEGORIES };

export interface EditBookSheetProps {
  book: BookRow;
  /** Set true to disable the delete affordance (e.g. last remaining book). */
  deleteDisabled?: boolean;
  onClose: () => void;
  onSave: (input: { name: string; color_token: string }) => void;
  onDelete: () => void;
  variant?: BooksSheetVariant;
}

export function EditBookSheet({
  book,
  deleteDisabled,
  onClose,
  onSave,
  onDelete,
  variant = 'sheet',
}: EditBookSheetProps): JSX.Element {
  const [name, setName] = useState(book.name);
  const [color, setColor] = useState<string>(book.color_token || '--terracotta-500');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const trimmed = name.trim();
  const dirty = trimmed !== book.name || color !== book.color_token;
  const canSave = trimmed.length > 0 && dirty;

  const submit = (): void => {
    if (!canSave) return;
    onSave({ name: trimmed, color_token: color });
  };

  const body = (
    <div>
      {/* Preview */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          padding: '12px 0 18px',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: tokenToCss(color),
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          <Icon name="book" size={28} stroke="#fff" />
        </div>
        <div
          style={{
            font: '600 16px var(--font-cjk)',
            color: 'var(--fg-1)',
            minHeight: 22,
          }}
        >
          {trimmed || book.name}
        </div>
      </div>

      {/* Name */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 6,
          padding: '0 2px',
        }}
      >
        名稱
      </div>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={16}
        style={{
          width: '100%',
          padding: '12px 14px',
          border: '0.5px solid var(--border-1)',
          borderRadius: 12,
          background: 'var(--bg-surface)',
          font: '500 15px var(--font-cjk)',
          color: 'var(--fg-1)',
          outline: 'none',
          boxSizing: 'border-box',
          marginBottom: 18,
        }}
      />

      {/* Color */}
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 8,
          padding: '0 2px',
        }}
      >
        顏色
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 10,
          padding: '6px 0 16px',
        }}
      >
        {COLOR_SWATCHES.map((c) => {
          const on = c.token === color;
          return (
            <button
              key={c.token}
              type="button"
              aria-label={c.label}
              aria-pressed={on}
              onClick={() => setColor(c.token)}
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                background: tokenToCss(c.token),
                border: on ? '2.5px solid var(--fg-1)' : '2.5px solid transparent',
                boxShadow: on ? 'inset 0 0 0 2px #fff' : 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          );
        })}
      </div>

      {/* Action row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          paddingTop: 6,
        }}
      >
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          disabled={deleteDisabled}
          aria-label="刪除帳本"
          title={deleteDisabled ? '至少要保留一本帳本' : '刪除帳本'}
          style={{
            padding: '10px 14px',
            background: 'transparent',
            border: '1px solid var(--border-1)',
            borderRadius: 999,
            cursor: deleteDisabled ? 'not-allowed' : 'pointer',
            opacity: deleteDisabled ? 0.5 : 1,
            font: '500 14px var(--font-cjk)',
            color: 'var(--expense)',
          }}
        >
          刪除
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              border: '1px solid var(--border-1)',
              borderRadius: 999,
              cursor: 'pointer',
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-2)',
            }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            style={{
              padding: '10px 18px',
              background: canSave ? 'var(--accent)' : 'var(--sand-300)',
              color: '#fff',
              border: 0,
              borderRadius: 999,
              cursor: canSave ? 'pointer' : 'default',
              font: '600 14px var(--font-cjk)',
            }}
          >
            儲存
          </button>
        </div>
      </div>

      {confirmDelete && (
        <div
          role="alertdialog"
          aria-label="刪除帳本確認"
          style={{
            marginTop: 14,
            padding: 14,
            background: 'var(--cream-100)',
            border: '0.5px solid var(--border-2)',
            borderRadius: 14,
          }}
        >
          <div
            style={{
              font: '500 14px/1.6 var(--font-cjk)',
              color: 'var(--fg-1)',
              marginBottom: 12,
            }}
          >
            確定刪除「{book.name}」？此帳本下的紀錄會保留但無法再被瀏覽。
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--border-1)',
                borderRadius: 999,
                cursor: 'pointer',
                font: '500 13px var(--font-cjk)',
                color: 'var(--fg-2)',
              }}
            >
              取消
            </button>
            <button
              type="button"
              onClick={onDelete}
              style={{
                padding: '8px 16px',
                background: 'var(--expense)',
                color: '#fff',
                border: 0,
                borderRadius: 999,
                cursor: 'pointer',
                font: '600 13px var(--font-cjk)',
              }}
            >
              確定刪除
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (variant === 'modal') {
    return (
      <Modal open={true} onClose={onClose} title="編輯帳本" width={420}>
        {body}
      </Modal>
    );
  }
  return (
    <BottomSheet open={true} onClose={onClose} title="編輯帳本">
      {body}
    </BottomSheet>
  );
}
